import { FC, ReactElement, ReactNode, useState } from 'react';
import IconArrowUp from '@shared/icons/IconArrowUp';

import './style.module.scss';
import SectionHeader from '@widgets/components/section-header';
import Search from '@widgets/components/search';
import IconArrowDown from '@shared/icons/IconArrowDown';
import { ISort, SortType } from '@entities/util/ISort';
import IconToast from '@shared/icons/IconToast';
import { InputCheckbox } from '@shared/uikit/input-checkbox';
import SingleSelect from '@widgets/single-select';
import ActionSelect from '@widgets/action-select';
import { Check, ChevronDown, ChevronsUpDown, ChevronUp, Minus } from 'lucide-react';
import { Checkbox } from '@shared/uikit/checkbox';
import clsx from 'clsx';

interface ITableProps {
  children: ReactElement[] | ReactElement;
}

export const Table: FC<ITableProps> = ({ children }) => {
  return <table className="w-full border-collapse pt-[30px]">{children}</table>;
};

interface ITableTitleAndSearch {
  title: string | ReactElement;
  search: string;
  setSearch: (value: string) => void;
}

export const TableTitleAndSearch: FC<ITableTitleAndSearch> = ({ title, search, setSearch }) => {
  return (
    <div className="flex justify-between items-center w-full mb-[20px]">
      <div>
        <SectionHeader title={title} />
      </div>
      <Search search={search} onSearch={setSearch} />
    </div>
  );
};

interface ITableSubTitle {
  title: string | ReactElement;
}

export const TableSubTitle: FC<ITableSubTitle> = ({ title }) => {
  return (
    <>
      <div className="flex justify-between items-center w-full">
        <span className="font-semibold text-[16px]">{title}</span>
      </div>
    </>
  );
};

interface ITableTheadProps {
  className?: string;
  children: ReactElement[] | ReactElement;
}

export const TableThead: FC<ITableTheadProps> = ({ children, className }) => {
  return (
    <thead
      className={clsx(
        'text-[12px] whitespace-nowrap sticky top-0 z-30',
        className,
        'bg-dark-default'
      )}
    >
      {children}
    </thead>
  );
};

interface ITableTbodyProps {
  children: any;
}

export const TableBody: FC<ITableTbodyProps> = ({ children }) => {
  return <tbody>{children}</tbody>;
};

interface ITableTrProps {
  children: ReactNode;
  shown?: boolean;
  bgColor?: string;
  withoutUnderline?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const TableTr: FC<ITableTrProps> = ({
  shown = true,
  bgColor = 'unset',
  withoutUnderline,
  children,
  className,
  style,
}) => {
  return (
    <tr
      className={`${shown ? '' : 'hidden'} text-left ${className ?? ''}`}
      style={{
        borderBottom: withoutUnderline ? 0 : undefined,
        ...style,
      }}
    >
      {children}
    </tr>
  );
};

interface ITableThProps {
  title: string | ReactElement;
  sortName?: string | null;
  sort?: ISort | null;
  className?: string | null;
  onSort?: (column: string) => void;
}

export const TableTh: FC<ITableThProps> = ({ title, className = '', sortName, sort, onSort }) => {
  return (
    <th className={`text-[#959595] p-4 font-medium fill-[#959595] ${className}`}>
      <div
        onClick={() => sortName && onSort && onSort(sortName)}
        className={`flex items-center ${
          className?.includes('text-left')
            ? 'justify-start'
            : sortName
              ? 'justify-between cursor-pointer hover:underline'
              : 'justify-end'
        }`}
      >
        <span>{title}</span>
        {sortName &&
          (sortName === sort?.column ? (
            sort?.type === SortType.ASC ? (
              <ChevronUp size={12} className="text-gray-400" />
            ) : (
              <ChevronDown size={12} className="text-gray-400" />
            )
          ) : (
            <ChevronsUpDown size={12} className="text-gray-400" />
          ))}
      </div>
    </th>
  );
};

type TableActionThProps = {
  onClick: () => void;
  className?: string;
  selectedCount?: number;
  totalCount?: number;
};

export const TableActionTh = ({
  onClick,
  className,
  selectedCount = 0,
  totalCount = 0,
}: TableActionThProps) => {
  const isAllSelected = selectedCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

  return (
    <th className={`text-gray-400 p-[10px] font-medium fill-gray-400 w-[10%] ${className}`}>
      <div className="flex items-center justify-center cursor-pointer" onClick={onClick}>
        <div
          className={clsx(
            'w-[14px] h-[14px] p-[1px] border border-gray-600 flex items-center justify-center rounded-sm',
            isAllSelected || isIndeterminate ? 'bg-blue-500' : 'bg-dark'
          )}
        >
          {isAllSelected && <Check size={14} color="white" />}
          {isIndeterminate && <Minus size={14} color="white" />}
        </div>
      </div>
    </th>
  );
};

type TableActionTdProps = {
  items: Array<number>;
  value: number;
  onChange: (value: number[]) => void;
  className?: string;
};

export const TableActionTd = ({ items, onChange, value, className }: TableActionTdProps) => {
  return (
    <td className={`align-center p-[10px] ${className}`}>
      <Checkbox
        label=""
        onChange={checked => {
          if (checked) {
            onChange([...items, value]);
          } else {
            onChange(items.filter(bulkItem => bulkItem !== value));
          }
        }}
        value={items.includes(value)}
      />
    </td>
  );
};

interface ITableTdProps {
  title?: string | ReactElement | null;
  align?: 'left' | 'right' | 'center';
  className?: string;
  colSpan?: number;
}

export const TableTd: FC<ITableTdProps> = ({ title, className, align, colSpan }) => {
  const justify =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <td className={`p-[11.5px] text-[#FFFFFF] ${className || ''}`} colSpan={colSpan}>
      <div className={`flex ${justify}`}>{title ?? '—'}</div>
    </td>
  );
};

interface ITableTdNestableProps {
  children: ReactElement[] | ReactElement | string | number;
  className?: string | null;
}

export const TableNestableRow: FC<ITableTdNestableProps> = ({ children }) => {
  return (
    <>
      <div className="flex justify-between py-[14px] px-[11.5px] border border-solid border-[#959595] border-x-0 border-opacity-30">
        {children}
      </div>
    </>
  );
};

export const TableNestableTd: FC<ITableTdNestableProps> = ({ className, children }) => {
  return (
    <div
      className={`flex gap-[10px] px-[10px] items-center text-[white] last:justify-end ${className}`}
    >
      {children}
    </div>
  );
};

export const TableNestableExpandIcon: FC<{
  shown: boolean;
  onClick: () => void;
}> = ({ shown, onClick }) => {
  return (
    <div
      className="bg-gradient-to-r from-[#4598E6] to-[#189CD8] w-[20px] h-[20px] rounded-[8px] flex justify-center items-center fill-[white] cursor-pointer"
      onClick={() => onClick()}
    >
      {shown ? <IconArrowDown /> : <IconArrowUp />}
    </div>
  );
};
